import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { PermissionGate } from "../PermissionGate";
import { LockOpen } from "lucide-react";
import { ControlSessionEnvelope } from "../../models/audit/controlSession";
import { PermissionId } from "../../models/permission";

interface AuditPageHeaderActionsProps {
  currentStatus: ControlSessionEnvelope["status"];
  loadingApprovalConfig: boolean;
  approvalRequired: boolean;
  isSubmitting: boolean;
  isReopening: boolean;
  handleSubmitForReview: () => void | Promise<void>;
  handleReopenSession: () => void | Promise<void>;
  isSessionComplete: boolean;
  isWorkStepSession: boolean;
  canEditLinkedControl: boolean;
  canEditLinkedWorkStep: boolean;
  controlSession: ControlSessionEnvelope | null;
  runTestPermission: PermissionId;
  editTestPermission: PermissionId;
}

export const AuditPageHeaderActions: React.FC<AuditPageHeaderActionsProps> = ({
  currentStatus,
  loadingApprovalConfig,
  approvalRequired,
  isSubmitting,
  isReopening,
  handleSubmitForReview,
  handleReopenSession,
  isSessionComplete,
  isWorkStepSession,
  canEditLinkedControl,
  canEditLinkedWorkStep,
  controlSession,
  runTestPermission,
  editTestPermission,
}) => {
  const navigate = useNavigate();

  return (
    <div className="flex items-center gap-2">
      {currentStatus === "in_progress" && !loadingApprovalConfig && (
        <PermissionGate permission={runTestPermission}>
          <Button
            onClick={handleSubmitForReview}
            disabled={isSubmitting}
            variant="outline"
          >
            {approvalRequired ? "Submit for Review" : "Mark as Complete"}
          </Button>
        </PermissionGate>
      )}
      {isSessionComplete && (
        <PermissionGate permission={editTestPermission}>
          <Button
            variant="outline"
            onClick={handleReopenSession}
            disabled={isReopening}
          >
            <LockOpen className="h-4 w-4 mr-2" />
            {isReopening ? "Reopening..." : "Reopen Session"}
          </Button>
        </PermissionGate>
      )}
      {isWorkStepSession ? (
        <Button
          variant="outline"
          onClick={() => {
            navigate(`/audit/work-steps/${controlSession?.workStepId}`);
          }}
        >
          {canEditLinkedWorkStep ? "Edit Work Step" : "View Work Step"}
        </Button>
      ) : (
        <Button
          variant="outline"
          onClick={() => {
            navigate(`/audit/controls/v2/${controlSession?.controlId}`);
          }}
        >
          {canEditLinkedControl ? "Edit Control" : "View Control"}
        </Button>
      )}
    </div>
  );
};
